import { useState, useEffect } from 'react';
import { ExternalLink, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Pagination } from '@/components/common/Pagination';
import { usePendencias } from '@/hooks/useUplog';
import { useQueryClient } from '@tanstack/react-query';
import type { Pendencia, PendenciaMes, RawLinkDoc } from '@/api/uplog.api';

const PAGE_SIZE = 20;

const MONTH_NAMES = [
  '',
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

function DocLinks({ doc, fallbackKey }: { doc: RawLinkDoc; fallbackKey: number }) {
  const boletos = doc.boletos ?? [];

  return (
    <div className="flex flex-wrap items-center gap-2">
      {doc.acessoNfse && (
        <a
          href={doc.acessoNfse}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-blue-600 hover:underline dark:text-blue-400"
        >
          NFS-e <ExternalLink className="h-3 w-3" />
        </a>
      )}

      {doc.boletim?.urlDownload && (
        <a
          href={doc.boletim.urlDownload}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-blue-600 hover:underline dark:text-blue-400"
        >
          Boletim <ExternalLink className="h-3 w-3" />
        </a>
      )}

      {boletos.map((b, i) =>
        b.urlDownload ? (
          <a
            key={i}
            href={b.urlDownload}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-emerald-600 hover:underline dark:text-emerald-400"
          >
            {b.nomeDocumento ?? `Boleto ${fallbackKey}.${i + 1}`}{' '}
            <ExternalLink className="h-3 w-3" />
          </a>
        ) : null,
      )}
    </div>
  );
}

function MesLinks({ mes }: { mes: PendenciaMes }) {
  const docs = mes.rawLinks ?? [];

  // Cada documento da Uplog tem sua própria NF + boleto(s) — nunca misturar a
  // NF de um documento com o boleto de outro (bug histórico: ver commit a250e9b).
  if (docs.length > 0) {
    return (
      <div className="text-xs">
        <span className="font-medium">
          {MONTH_NAMES[mes.mes]}/{mes.ano}
        </span>
        <div className="mt-1 space-y-1">
          {docs.map((doc, i) => (
            <DocLinks key={i} doc={doc} fallbackKey={i + 1} />
          ))}
        </div>
      </div>
    );
  }

  const boletos = mes.boletos ?? [];
  const hasAny = mes.nfseUrl || mes.boletimUrl || boletos.length > 0;

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="font-medium w-12 shrink-0">
        {MONTH_NAMES[mes.mes]}/{mes.ano}
      </span>

      {mes.nfseUrl && (
        <a
          href={mes.nfseUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-blue-600 hover:underline dark:text-blue-400"
        >
          NFS-e <ExternalLink className="h-3 w-3" />
        </a>
      )}

      {mes.boletimUrl && (
        <a
          href={mes.boletimUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-blue-600 hover:underline dark:text-blue-400"
        >
          Boletim <ExternalLink className="h-3 w-3" />
        </a>
      )}

      {boletos.map((b, i) =>
        b.urlDownload ? (
          <a
            key={i}
            href={b.urlDownload}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-emerald-600 hover:underline dark:text-emerald-400"
          >
            {b.nomeDocumento ?? `Boleto ${i + 1}`} <ExternalLink className="h-3 w-3" />
          </a>
        ) : null,
      )}

      {!hasAny && <span className="text-muted-foreground">Sem links</span>}
    </div>
  );
}

function PendenciaRow({ pendencia }: { pendencia: Pendencia }) {
  const [expanded, setExpanded] = useState(false);
  const mesesCount = pendencia.meses.length;

  return (
    <div className="border rounded-md overflow-hidden">
      <button
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors text-left"
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
        )}
        <span className="font-mono text-sm font-medium flex-1">{pendencia.documento}</span>
        <Badge variant="secondary" className="shrink-0">
          {mesesCount} {mesesCount === 1 ? 'mês' : 'meses'}
        </Badge>
        <span className="text-xs text-muted-foreground shrink-0">
          Atualizado {new Date(pendencia.lastSeenAt).toLocaleDateString('pt-BR')}
        </span>
      </button>

      {expanded && (
        <div className="border-t px-4 py-3 space-y-2 bg-muted/20">
          {pendencia.meses.length === 0 ? (
            <p className="text-xs text-muted-foreground">Nenhum mês encontrado.</p>
          ) : (
            pendencia.meses.map((m) => <MesLinks key={m.id} mes={m} />)
          )}
        </div>
      )}
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full rounded-md" />
      ))}
    </div>
  );
}

export function PendenciasTable() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const params = { page, pageSize: PAGE_SIZE, search: debouncedSearch || undefined };
  const { data: result, isLoading, dataUpdatedAt } = usePendencias(params);

  const pendencias = result?.data ?? [];
  const meta = result?.meta;

  const updatedAt = dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString('pt-BR') : null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle>Pendências</CardTitle>
          <CardDescription>
            Clientes com pendências financeiras detectadas pelo scanner.
            {updatedAt && <span className="ml-1">Atualizado às {updatedAt}.</span>}
          </CardDescription>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            void qc.invalidateQueries({ queryKey: ['uplog', 'pendencias'] });
          }}
          title="Atualizar"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <Input
          placeholder="Buscar por documento…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />

        {isLoading && <TableSkeleton />}

        {!isLoading && pendencias.length === 0 && (
          <p className="text-sm text-muted-foreground">
            {debouncedSearch
              ? 'Nenhuma pendência encontrada para esta busca.'
              : 'Nenhuma pendência encontrada. Execute um scan para carregar dados.'}
          </p>
        )}

        {pendencias.length > 0 && (
          <div className="space-y-2">
            {pendencias.map((p) => (
              <PendenciaRow key={p.id} pendencia={p} />
            ))}
          </div>
        )}

        {meta && meta.totalPages > 1 && (
          <Pagination
            page={meta.page}
            totalPages={meta.totalPages}
            total={meta.total}
            pageSize={meta.pageSize}
            onPageChange={setPage}
          />
        )}
      </CardContent>
    </Card>
  );
}
